import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Title1,
  Subtitle2,
  TabList,
  Tab,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Spinner,
  Switch,
  Dropdown,
  Option,
  tokens,
} from '@fluentui/react-components'
import { apiFetchJson } from '../lib/apiFetch'
import { useMe } from '../lib/me'
import { SERVICE_LABELS } from '../lib/services'
import Lozenge from '../components/Lozenge'
import type { ActivityLogsResponse } from '../types'

type TabValue = 'activity' | 'sync-runs'

const VALID_TABS: TabValue[] = ['activity', 'sync-runs']

const ACTION_INFO: Record<string, { label: string; color: 'brand' | 'informative' | 'success' | 'danger' | 'subtle' }> = {
  'person-link.set': { label: 'Person link', color: 'brand' },
  'person-link.clear': { label: 'Clear override', color: 'subtle' },
  'github-link.set': { label: 'GitHub link', color: 'brand' },
  'github-link.clear': { label: 'GitHub clear', color: 'subtle' },
  'settings.update': { label: 'Settings', color: 'informative' },
  'service-config.set': { label: 'Service config', color: 'informative' },
  'license-type.set': { label: 'License type', color: 'informative' },
  'access.set': { label: 'Access grant', color: 'brand' },
  'access.clear': { label: 'Access revoke', color: 'danger' },
  'sync.trigger': { label: 'Manual sync', color: 'success' },
  'sync.clear-assignments': { label: 'Clear assignments', color: 'danger' },
  'directory-sync.trigger': { label: 'Directory sync', color: 'success' },
  'csv.upload': { label: 'CSV import', color: 'success' },
}

function serviceLabel(serviceId: string | null): string {
  if (!serviceId) return '—'
  if (serviceId === 'directory') return 'Directory (Entra)'
  if (serviceId === 'peopleforce') return 'PeopleForce'
  return SERVICE_LABELS[serviceId] ?? serviceId
}

function useActivityLogs() {
  const [data, setData] = useState<ActivityLogsResponse | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    apiFetchJson<ActivityLogsResponse>('/api/activity-logs')
      .then(setData)
      .catch(e => setError(String(e)))
  }, [])

  return { data, error }
}

function ActivityTab() {
  const { data, error } = useActivityLogs()

  if (error) return <p>Error: {error}</p>
  if (!data) return <Spinner label="Loading activity..." style={{ marginTop: 24 }} />
  if (data.logs.length === 0) return <p>No admin activity recorded yet.</p>

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHeaderCell style={{ width: 170 }}>Timestamp</TableHeaderCell>
          <TableHeaderCell style={{ width: 200 }}>Actor</TableHeaderCell>
          <TableHeaderCell style={{ width: 150 }}>Action</TableHeaderCell>
          <TableHeaderCell>Summary</TableHeaderCell>
          <TableHeaderCell style={{ width: 130 }}>Service</TableHeaderCell>
        </TableRow>
      </TableHeader>
      <TableBody>
        {data.logs.map((log, i) => {
          const info = ACTION_INFO[log.action]
          return (
            <TableRow key={i}>
              <TableCell>{log.timestamp}</TableCell>
              <TableCell>
                <div>{log.actorDisplayName}</div>
                <div style={{ fontSize: 12, color: tokens.colorNeutralForeground3 }}>{log.actorEmail}</div>
              </TableCell>
              <TableCell>
                <Lozenge color={info?.color ?? 'subtle'}>{info?.label ?? log.action}</Lozenge>
              </TableCell>
              <TableCell>{log.summary}</TableCell>
              <TableCell>{serviceLabel(log.serviceId)}</TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}

function SyncRunsTab() {
  const { data, error } = useActivityLogs()
  const [serviceFilter, setServiceFilter] = useState<string>('all')
  const [latestOnly, setLatestOnly] = useState(false)

  if (error) return <p>Error: {error}</p>
  if (!data) return <Spinner label="Loading sync runs..." style={{ marginTop: 24 }} />
  if (data.syncRuns.length === 0) return <p>No sync runs recorded yet.</p>

  const services = [...new Set(data.syncRuns.map(r => r.serviceId))].sort((a, b) =>
    serviceLabel(a).localeCompare(serviceLabel(b)),
  )
  let rows = serviceFilter === 'all' ? data.syncRuns : data.syncRuns.filter(r => r.serviceId === serviceFilter)
  // Backend already returns newest-first, so the first row per service is its latest run.
  if (latestOnly) {
    const seen = new Set<string>()
    rows = rows.filter(r => {
      if (seen.has(r.serviceId)) return false
      seen.add(r.serviceId)
      return true
    })
  }

  return (
    <>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
        <Dropdown
          placeholder="Service"
          selectedOptions={[serviceFilter]}
          value={serviceFilter === 'all' ? 'All services' : serviceLabel(serviceFilter)}
          onOptionSelect={(_e, d) => setServiceFilter((d.optionValue as string) ?? 'all')}
          style={{ minWidth: 200 }}
        >
          <Option value="all">All services</Option>
          {services.map(s => (
            <Option key={s} value={s}>
              {serviceLabel(s)}
            </Option>
          ))}
        </Dropdown>
        <Switch
          label="Latest run per service only"
          checked={latestOnly}
          onChange={(_e, d) => setLatestOnly(d.checked)}
        />
        <span style={{ fontSize: 13, color: tokens.colorNeutralForeground3 }}>
          {rows.length} {rows.length === 1 ? 'run' : 'runs'}
        </span>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHeaderCell style={{ width: 150 }}>Service</TableHeaderCell>
            <TableHeaderCell style={{ width: 170 }}>Started</TableHeaderCell>
            <TableHeaderCell style={{ width: 170 }}>Finished</TableHeaderCell>
            <TableHeaderCell style={{ width: 90 }}>Method</TableHeaderCell>
            <TableHeaderCell style={{ width: 110 }}>Records</TableHeaderCell>
            <TableHeaderCell>Errors</TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((run, i) => {
            const errors = JSON.parse(run.errorsJson) as string[]
            return (
              <TableRow key={i}>
                <TableCell>{serviceLabel(run.serviceId)}</TableCell>
                <TableCell>{run.startedAt}</TableCell>
                <TableCell>{run.finishedAt ?? '—'}</TableCell>
                <TableCell>{run.method}</TableCell>
                <TableCell>{run.recordsProcessed}</TableCell>
                <TableCell>
                  {errors.length === 0 ? (
                    '—'
                  ) : (
                    <>
                      <Lozenge color="danger">{errors.length} errors</Lozenge>
                      <div style={{ fontSize: 12, color: tokens.colorNeutralForeground3, marginTop: 4 }}>
                        {errors.join(' ')}
                      </div>
                    </>
                  )}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </>
  )
}

export default function Logs() {
  const [searchParams, setSearchParams] = useSearchParams()
  const me = useMe()
  const requestedTab = searchParams.get('tab')
  const activeTab: TabValue = VALID_TABS.includes(requestedTab as TabValue) ? (requestedTab as TabValue) : 'activity'

  function handleTabSelect(value: TabValue) {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      next.set('tab', value)
      return next
    })
  }

  if (!me.isAdmin) {
    return (
      <div>
        <Title1>Logs</Title1>
        <Subtitle2 style={{ display: 'block', marginTop: 16 }}>Admin access required.</Subtitle2>
      </div>
    )
  }

  return (
    <div>
      <Title1>Logs</Title1>
      <Subtitle2 style={{ display: 'block', marginTop: 4, marginBottom: 16 }}>
        Admin activity and sync run history — kept per the retention period set on the Settings page
      </Subtitle2>

      <TabList selectedValue={activeTab} onTabSelect={(_e, data) => handleTabSelect(data.value as TabValue)} style={{ marginBottom: 16 }}>
        <Tab value="activity">Activity</Tab>
        <Tab value="sync-runs">Sync runs</Tab>
      </TabList>

      {activeTab === 'activity' && <ActivityTab />}
      {activeTab === 'sync-runs' && <SyncRunsTab />}
    </div>
  )
}
